<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { usePins } from './usePins';

// Top of the conversation list: the agent's pinned conversations, whatever the current filter
const { t } = useLocoI18n();
const route = useRoute();
const router = useRouter();
const { pinned, toggle } = usePins();

const collapsed = ref(false);

const activeId = computed(() => Number(route.params.conversation_id));

const preview = conversation => {
  const message = conversation.last_non_activity_message;
  if (!message) return '';
  return message.content || message.attachments?.[0]?.file_type || '';
};

const open = conversation => {
  router.push(
    frontendURL(
      conversationUrl({
        accountId: route.params.accountId,
        id: conversation.id,
      })
    )
  );
};
</script>

<template>
  <div v-if="pinned.length" class="px-2 pt-1 pb-2 border-b border-n-weak">
    <button
      type="button"
      class="flex items-center w-full gap-1.5 px-2 py-1 text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
      @click="collapsed = !collapsed"
    >
      <Icon icon="i-lucide-pin" class="size-3" />
      {{ t('LOCO.PINS.SECTION', { count: pinned.length }) }}
      <Icon
        :icon="collapsed ? 'i-lucide-chevron-right' : 'i-lucide-chevron-down'"
        class="size-3 ms-auto"
      />
    </button>
    <ul v-if="!collapsed" class="flex flex-col">
      <li
        v-for="conversation in pinned"
        :key="conversation.id"
        class="group flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-2': conversation.id === activeId }"
        @click="open(conversation)"
      >
        <Avatar
          :name="conversation.meta.sender.name"
          :src="conversation.meta.sender.thumbnail"
          :size="24"
          rounded-full
        />
        <div class="flex flex-col min-w-0 flex-1">
          <span class="text-sm text-n-slate-12 truncate">
            {{ conversation.meta.sender.name }}
          </span>
          <span class="text-xs text-n-slate-11 truncate">
            {{ preview(conversation) }}
          </span>
        </div>
        <span
          v-if="conversation.unread_count"
          class="flex-shrink-0 min-w-4 h-4 px-1 rounded-full bg-n-brand text-white text-[10px] leading-4 text-center"
        >
          {{ conversation.unread_count }}
        </span>
        <button
          type="button"
          class="opacity-0 group-hover:opacity-100 text-n-slate-10 hover:text-n-slate-12"
          :title="t('LOCO.PINS.UNPIN')"
          @click.stop="toggle(conversation.id)"
        >
          <Icon icon="i-lucide-pin-off" class="size-3.5" />
        </button>
      </li>
    </ul>
  </div>
</template>
